/**
 * EventService - Captures and manages SIP/RTP events for DevTools
 */

import { createLogger } from 'Modules/softphone/utils/logger';

const logger = createLogger('EventService');

export type DeviceChangeType = 'microphone' | 'speaker' | 'ringer';

export interface SoftphoneEvent {
  id: string;
  type: 'SIP' | 'RTP' | 'DEVICE' | 'CALL';
  /**
   * Which way the message travelled.
   *
   * Optional on purpose: a call lifecycle event did not travel anywhere, and
   * picking a direction for it would draw an arrow for traffic that may never
   * have existed.
   */
  direction?: 'incoming' | 'outgoing';
  message: string;
  timestamp: string;
  /**
   * The call this event belongs to, when it belongs to one.
   *
   * Optional on purpose: registration traffic and device switches belong to no
   * call, and forcing an id on them would mean inventing one. Events without a
   * callId are attributed by timestamp window instead (see DebugService).
   */
  callId?: string;
  /** The conference this event belongs to, for room-level events. */
  conferenceId?: string;
  details?: Record<string, any>;
}

type EventListener = (event: SoftphoneEvent) => void;

/** Notified whenever the buffer changes, with no event to hand over. */
type ChangeListener = () => void;

/**
 * Read a call/conference id out of an event's `details` bag.
 * Anything that is not a non-empty string is ignored.
 */
const readId = (
  details: Record<string, any> | undefined,
  key: 'callId' | 'conferenceId'
): string | undefined => {
  const value = details?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

export class EventService {
  private static instance: EventService;
  private events: SoftphoneEvent[] = [];
  private listeners: Set<EventListener> = new Set();
  private changeListeners: Set<ChangeListener> = new Set();
  /**
   * One shared ring buffer across every call, the registration traffic and the
   * device switches.
   *
   * 100 was enough while only one call was ever on the wire. A warm transfer is
   * two legs and a four-party conference is five sessions, each with its own
   * INVITE / ICE / RTP / track traffic plus the room's REFERs — so the start of
   * the very scenario being investigated could be evicted before it ended.
   * Raised so that a multi-leg interaction survives whole.
   */
  private maxEvents = 500;

  private constructor() {}

  static getInstance(): EventService {
    if (!EventService.instance) {
      EventService.instance = new EventService();
    }
    return EventService.instance;
  }

  /**
   * Add a new event.
   *
   * Never throws. Recording is now on the call path in several places — every
   * message the transport receives or sends, every session state change, every
   * answer and decline — and an observer must never break what it observes. A
   * few call sites wrapped themselves; guarding the single funnel makes that
   * true for all of them, including the ones written next year.
   */
  addEvent(event: Omit<SoftphoneEvent, 'id' | 'timestamp'>): void {
    try {
      this.record(event);
    } catch (error) {
      logger.error('Failed to record event', error as Error);
    }
  }

  private record(event: Omit<SoftphoneEvent, 'id' | 'timestamp'>): void {
    const newEvent: SoftphoneEvent = {
      ...event,
      // `{ callId }` inside `details` was already the convention at every call
      // site, so it is promoted to a first-class field rather than requiring
      // every emitter to pass it twice. An explicit field always wins.
      callId: event.callId ?? readId(event.details, 'callId'),
      conferenceId: event.conferenceId ?? readId(event.details, 'conferenceId'),
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
    };

    this.events.push(newEvent);

    // Keep only last maxEvents
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(-this.maxEvents);
    }

    // Notify listeners
    this.listeners.forEach((listener) => {
      try {
        listener(newEvent);
      } catch (error) {
        logger.error('Error in event listener', error as Error);
      }
    });

    this.notifyChange();

    logger.debug('Event added', { type: event.type, message: event.message });
  }

  /**
   * Subscribe to buffer changes — anything that alters what `getAll` returns,
   * a clear included.
   *
   * Separate from `subscribe`, which only reports new events: a counter built
   * on that alone stays stuck at the last count once the buffer is emptied,
   * because emptying it produces no event to report.
   */
  onChange(listener: ChangeListener): () => void {
    this.changeListeners.add(listener);
    return () => {
      this.changeListeners.delete(listener);
    };
  }

  private notifyChange(): void {
    this.changeListeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        logger.error('Error in change listener', error as Error);
      }
    });
  }

  /**
   * Subscribe to events
   */
  subscribe(listener: EventListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Get all events
   */
  getAll(): SoftphoneEvent[] {
    return [...this.events];
  }

  /**
   * How many events the ring buffer holds before it starts evicting.
   *
   * Exposed so the panel can show how close a scenario is to losing its own
   * beginning, rather than leaving the cap to be discovered by its effects.
   */
  getMaxEvents(): number {
    return this.maxEvents;
  }

  /**
   * Clear all events
   */
  clear(): void {
    this.events = [];
    this.notifyChange();
    logger.debug('Events cleared');
  }

  /**
   * Log SIP message
   */
  logSipMessage(
    direction: 'incoming' | 'outgoing',
    message: string,
    details?: Record<string, any>
  ): void {
    this.addEvent({
      type: 'SIP',
      direction,
      message,
      details,
    });
  }

  /**
   * Log a call lifecycle event — what the session did, not what crossed the wire.
   *
   * Kept apart from `logSipMessage` deliberately. A session reaching its
   * terminated state may follow a BYE, a CANCEL, a 4xx or a purely local hangup
   * that put nothing on the wire at all, so filing it under SIP would claim
   * signalling that never happened. The SIP channel carries the messages the
   * transport actually saw (see `SipService`); this one carries state changes.
   */
  logCallEvent(message: string, details?: Record<string, any>): void {
    this.addEvent({
      type: 'CALL',
      message,
      details,
    });
  }

  /**
   * Log RTP event
   */
  logRtpEvent(
    direction: 'incoming' | 'outgoing',
    message: string,
    details?: Record<string, any>
  ): void {
    this.addEvent({
      type: 'RTP',
      direction,
      message,
      details,
    });
  }

  /**
   * Log an audio device switch (microphone/speaker/ringer).
   * Captured so the Debug tab can render a device-change timeline per call.
   */
  logDeviceChange(
    deviceType: DeviceChangeType,
    fromLabel: string | null,
    toLabel: string,
    details?: Record<string, any>
  ): void {
    this.addEvent({
      type: 'DEVICE',
      direction: 'outgoing',
      message: `${deviceType} changed: ${fromLabel ?? 'None'} → ${toLabel}`,
      details: { deviceType, fromLabel, toLabel, ...details },
    });
  }
}
